import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Flower2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";

const EMPTY = {
  name: "", scientific_name: "", color: "", description: "", season: "", active: true,
};

export default function FlowerTypes() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/flower-types").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/flower-types/${editing}`, form);
      else await api.post("/flower-types", form);
      toast.success(editing ? "Tipo actualizado" : "Tipo creado");
      setForm(EMPTY); setEditing(null); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onEdit = (i) => { setForm({ ...EMPTY, ...i }); setEditing(i.id); setShowForm(true); };
  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar tipo de flor?")) return;
    try { await api.delete(`/flower-types/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="flower-types-page">
      <PageHeader
        title="Tipos de Flores"
        subtitle="Catálogo de variedades disponibles para productos."
        testid="flower-types-header"
        action={
          <button data-testid="flower-types-create-button"
            onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nuevo tipo
          </button>
        }
      />

      {showForm && (
        <div data-testid="flower-type-form" className="card-soft p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-bold">{editing ? "Editar" : "Nuevo"} tipo de flor</h3>
            <button onClick={() => setShowForm(false)} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre *"><input data-testid="flower-type-name" required className="input-soft" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Nombre científico"><input className="input-soft" value={form.scientific_name || ""} onChange={(e) => setForm({ ...form, scientific_name: e.target.value })} /></Field>
            <Field label="Color"><input className="input-soft" placeholder="ej. Rojo intenso" value={form.color || ""} onChange={(e) => setForm({ ...form, color: e.target.value })} /></Field>
            <Field label="Temporada"><input className="input-soft" placeholder="ej. Todo el año" value={form.season || ""} onChange={(e) => setForm({ ...form, season: e.target.value })} /></Field>
            <div className="md:col-span-2">
              <Field label="Descripción"><textarea rows={2} className="input-soft" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            </div>
            <div className="md:col-span-2 flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
              <button data-testid="flower-type-save-button" className="btn-primary" type="submit">Guardar</button>
            </div>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="Sin tipos cargados" hint="Registra rosas, tulipanes, lirios, etc." testid="flower-types-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="flower-types-grid">
          {items.map((f) => (
            <div key={f.id} className="card-soft p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F6E5DD] grid place-items-center">
                    <Flower2 className="w-5 h-5 text-[#C27A62]" strokeWidth={1.7} />
                  </div>
                  <div>
                    <p className="font-heading text-lg font-bold">{f.name}</p>
                    {f.scientific_name && <p className="text-xs italic text-[#8A9A92]">{f.scientific_name}</p>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => onEdit(f)} className="p-2 rounded hover:bg-[#F3F2ED]"><Pencil className="w-4 h-4 text-[#5C6B64]" /></button>
                  <button onClick={() => onDelete(f.id)} className="p-2 rounded hover:bg-[#F3DADA]"><Trash2 className="w-4 h-4 text-[#A44A4A]" /></button>
                </div>
              </div>
              {f.color && <p className="text-sm text-[#5C6B64] mt-3"><span className="text-[11px] uppercase tracking-wider text-[#8A9A92]">Color</span><br />{f.color}</p>}
              {f.season && <p className="text-sm text-[#5C6B64] mt-2"><span className="text-[11px] uppercase tracking-wider text-[#8A9A92]">Temporada</span><br />{f.season}</p>}
              {f.description && <p className="text-sm text-[#5C6B64] mt-3 line-clamp-2">{f.description}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
